import * as React from 'react';
import { Button, CircularProgress, makeStyles, Tooltip } from '@material-ui/core';
import { Error, GetApp } from '@material-ui/icons';
import { blue, status } from '../../../theme/colors';
import { fontFamilies } from '../../../theme/text';
import { useSegmentExport } from '../../../hooks/useSegmentExport';
import useTimedAlerts from '../../../hooks/useTimedAlerts';
import moment from 'moment';
import { SegmentAttributeGroup } from '../../../models/segments';
import { datadogRum } from '@datadog/browser-rum';

interface Props {
  name: string;
  includedAttributes: SegmentAttributeGroup;
  excludedAttributes: SegmentAttributeGroup;
  segmentId: string;
  segmentShared: string;
  userName: string;
}

const useStyles = makeStyles({
  button: {
    '&.MuiButton-root': {
      fontFamily: fontFamilies.heading,
      fontSize: 11
    },
    '&.MuiButton-root:not(.Mui-disabled)': {
      color: blue[600]
    }
  }
});

const ExportSegment = ({ name, includedAttributes, excludedAttributes, segmentId, segmentShared, userName}: Props) => {
  const overrides = useStyles();
  const exportQuery = useSegmentExport();
  const { errorMessage, setErrorMessage } = useTimedAlerts();

  const requestExport = () => {
    exportQuery.mutate(
      {
        fileName: name,
        attributes: {
          include: includedAttributes,
          exclude: excludedAttributes
        }
      },
      {
        onSuccess: data => {
          const url = URL.createObjectURL(data);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${name || 'Untitled Segment'} ${moment().format('YYYY-MM-DD')}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          datadogRum.addUserAction('download-segment', {
            user_action_details: {
              segment_id: segmentId,
              segment_name: name,
              is_segment_shared: segmentShared,
              user_name: userName
            }
          });
        }
      }
    );
  };

  React.useEffect(() => {
    if (exportQuery.isError) {
      setErrorMessage(exportQuery.error.message);
    }
  }, [exportQuery.isError]);

  React.useEffect(() => {
    if (exportQuery.isLoading) {
      setErrorMessage('');
    }
  }, [exportQuery.isLoading]);

  return (
    <React.Fragment>
      {exportQuery.isLoading && <CircularProgress size="16px" />}
      {errorMessage && (
        <Tooltip title={errorMessage}>
          <Error htmlColor={status.error.dark} />
        </Tooltip>
      )}
      <Button
        startIcon={<GetApp />}
        onClick={requestExport}
        className={overrides.button}
        disabled={includedAttributes.predicates.length === 0 && excludedAttributes.predicates.length === 0}
      >
        Download
      </Button>
    </React.Fragment>
  );
};

export default ExportSegment;
